import type { ReactNode } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { HoverInfo } from "../../../components/core/basic/Tooltip";
import { RadioGroup } from "../../../components/fields/base/RadioGroup";
import { TagMultiSelect } from "../../tags/components/TagMultiSelect";

type HarvestingSelectionMode = "ads" | "tags" | "both";
type HarvestingMatch = "all" | "any";

interface HarvestingSelectionValue {
  mode: HarvestingSelectionMode;
  ad_ids: number[];
  tag_ids: number[];
  tag_match: HarvestingMatch;
  combine_match: HarvestingMatch;
}

export const defaultHarvestingSelection: HarvestingSelectionValue = {
  mode: "ads",
  ad_ids: [],
  tag_ids: [],
  tag_match: "any",
  combine_match: "any",
};

interface Props {
  fieldName: "sources_selection" | "targets_selection";
  renderAdSelector: (args: {
    selectedAdvertisementIds: number[];
    onAdvertisementChange: (ids: number[]) => void;
    isReadOnly?: boolean;
  }) => ReactNode;
  isReadOnly?: boolean;
  enableTags?: boolean;
}

export function HarvestingAdSelection({ fieldName, renderAdSelector, isReadOnly, enableTags = true }: Props) {
  const { control } = useFormContext();
  const { t } = useTranslation(["advertising"]);

  const modeOptions = [
    { value: "ads", label: t(($) => $.advertising["Specific ads"]) },
    { value: "tags", label: t(($) => $.advertising["Tags"]) },
    { value: "both", label: t(($) => $.advertising["Both"]) },
  ];

  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field }) => {
        const value: HarvestingSelectionValue = field.value ?? defaultHarvestingSelection;
        const update = (patch: Partial<HarvestingSelectionValue>) => {
          field.onChange({ ...value, ...patch });
        };

        if (!enableTags) {
          return (
            <VStack css={{ width: "full", gap: "medium", alignItems: "start" }}>
              {renderAdSelector({
                selectedAdvertisementIds: value.ad_ids,
                onAdvertisementChange: (ids) => update({ ad_ids: ids }),
                isReadOnly,
              })}
            </VStack>
          );
        }

        const showAds = value.mode === "ads" || value.mode === "both";
        const showTags = value.mode === "tags" || value.mode === "both";

        return (
          <VStack css={{ width: "full", gap: "medium", alignItems: "start" }}>
            <RadioGroup
              orientation="horizontal"
              justify="start"
              aria-label={t(($) => $.advertising["Selection mode"])}
              value={value.mode}
              onChange={(v) => update({ mode: v as HarvestingSelectionMode })}
              items={modeOptions}
              isDisabled={isReadOnly}
            />
            {value.mode === "both" && (
              <RadioGroup
                orientation="horizontal"
                justify="start"
                label={t(($) => $.advertising["Should tags narrow your selection, or add to it?"])}
                aria-label={t(($) => $.advertising["Combine mode"])}
                value={value.combine_match}
                onChange={(v) => update({ combine_match: v as HarvestingMatch })}
                items={[
                  {
                    value: "any",
                    label: (
                      <HStack gap="xsmall" alignItems="center">
                        {t(($) => $.advertising["Add"])}
                        <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                          {t(($) => $.advertising["Also include any media plan with these tags"])}
                        </HoverInfo>
                      </HStack>
                    ),
                  },
                  {
                    value: "all",
                    label: (
                      <HStack gap="xsmall" alignItems="center">
                        {t(($) => $.advertising["Narrow"])}
                        <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                          {t(($) => $.advertising["Only my selected media plans that have these tags"])}
                        </HoverInfo>
                      </HStack>
                    ),
                  },
                ]}
                isDisabled={isReadOnly}
              />
            )}
            {showTags && (
              <VStack gap="medium" alignItems="start" className={css({ width: "full" })}>
                <TagMultiSelect
                  entityType="ADVERTISEMENT"
                  value={value.tag_ids}
                  onChange={(ids) => update({ tag_ids: ids })}
                  ariaLabel={t(($) => $.advertising["Select tags"])}
                  isDisabled={isReadOnly}
                />
                {value.tag_ids.length > 1 && (
                  <RadioGroup
                    orientation="horizontal"
                    justify="start"
                    label={t(($) => $.advertising["How should multiple tags match?"])}
                    aria-label={t(($) => $.advertising["Tag match"])}
                    value={value.tag_match}
                    onChange={(v) => update({ tag_match: v as HarvestingMatch })}
                    items={[
                      {
                        value: "any",
                        label: (
                          <HStack gap="xsmall" alignItems="center">
                            {t(($) => $.advertising["Any"])}
                            <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                              {t(($) => $.advertising["Media plans with any of these tags"])}
                            </HoverInfo>
                          </HStack>
                        ),
                      },
                      {
                        value: "all",
                        label: (
                          <HStack gap="xsmall" alignItems="center">
                            {t(($) => $.advertising["All"])}
                            <HoverInfo iconCss={{ color: "grey", fontSize: "small" }}>
                              {t(($) => $.advertising["Media plans must have all of these tags"])}
                            </HoverInfo>
                          </HStack>
                        ),
                      },
                    ]}
                    isDisabled={isReadOnly}
                  />
                )}
              </VStack>
            )}

            {showAds &&
              renderAdSelector({
                selectedAdvertisementIds: value.ad_ids,
                onAdvertisementChange: (ids) => update({ ad_ids: ids }),
                isReadOnly,
              })}
          </VStack>
        );
      }}
    />
  );
}
